import { Badge, Button, formatMoney, Skeleton, type StatusTone } from '@platform/design-system';
import { Link2, RotateCw } from 'lucide-react';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import { accountOf, isConfigurable, type Carrier } from './carriers.ts';
import styles from './Settings.module.css';

type CarrierState = 'connected' | 'paused' | 'notConnected' | 'soon';

const STATE_TONES: Readonly<Record<CarrierState, StatusTone>> = {
  connected: 'success',
  paused: 'warning',
  notConnected: 'neutral',
  soon: 'neutral',
};

export const carrierStateOf = (carrier: Carrier): CarrierState => {
  if (!isConfigurable(carrier)) return 'soon';
  const account = accountOf(carrier);
  if (account === undefined) return 'notConnected';
  return account.enabled ? 'connected' : 'paused';
};

export type CarrierTilesProps = {
  query: {
    data: { items: readonly Carrier[] } | undefined;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  canManage: boolean;
  onEdit: (carrier: Carrier) => void;
};

export const CarrierTiles = ({ query, canManage, onEdit }: CarrierTilesProps) => {
  const { t, locale, describeError } = usePageContext();
  if (query.isError)
    return <LoadFailed description={describeError(query.error)} onRetry={() => void query.refetch()} />;
  if (query.data === undefined) return <Skeleton height="120px" label={t('carriers.title')} />;
  return (
    <ul className={styles['tiles']}>
      {query.data.items.map((carrier) => {
        const state = carrierStateOf(carrier);
        const account = accountOf(carrier);
        return (
          <li
            key={carrier.code}
            className={[styles['tile'], state === 'soon' ? styles['tileMuted'] : ''].join(' ')}
          >
            <div className={styles['tileHead']}>
              <span className={styles['tileMark']} aria-hidden="true">
                {carrier.name.charAt(0).toUpperCase()}
              </span>
              <span className={styles['tileTitle']}>
                <strong>{carrier.name}</strong>
                <Badge tone={STATE_TONES[state]}>{t(`carriers.state_${state}`)}</Badge>
              </span>
            </div>
            <p className={styles['muted']}>{t(`carriers.state_${state}Help`)}</p>
            {account === undefined ? null : (
              <p className={styles['secret']}>
                {t('carriers.secretHint')}{' '}
                <bdi dir="ltr" className={styles['nowrap']}>
                  …{account.fingerprint}
                </bdi>
              </p>
            )}
            {account?.customerFee == null ? null : (
              <p className={styles['muted']}>
                {t('carriers.customerFeeShown', { fee: formatMoney(account.customerFee, locale) })}
              </p>
            )}
            {canManage && state !== 'soon' ? (
              <Button
                variant={state === 'notConnected' ? 'primary' : 'outline'}
                size="sm"
                icon={
                  state === 'notConnected' ? (
                    <Link2 size={16} aria-hidden="true" />
                  ) : (
                    <RotateCw size={16} aria-hidden="true" />
                  )
                }
                onClick={() => {
                  onEdit(carrier);
                }}
              >
                {state === 'notConnected' ? t('carriers.connect') : t('carriers.changeCredentials')}
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
